import type { PlanInterval } from './planQuote';

/** The two consent boxes (TMMAPP-0118 §A1). */
export interface ConsentValues {
  terms: boolean;
  autoRenewal: boolean;
}

/** Both boxes start unchecked, every time the dialog opens. */
export const NO_CONSENT: ConsentValues = { terms: false, autoRenewal: false };

/** What box 2 states, from the served quote. */
export interface RenewalTerms {
  /** The chosen interval, as priced. */
  interval: PlanInterval;
  /**
   * The interval's served total **without** a promotion (TMMAPP-0119 item 14): the regular price
   * the subscription renews at, from the quote fetched without `promotionCode`.
   */
  priceMinor: number;
  currency: string;
  /** The trial's end, a civil `YYYY-MM-DD` (locale-free, so a language change keeps the tick); null with no trial. */
  trialEnd: string | null;
  /**
   * A price promotion the person applied: its code and served statement count (null when not
   * served). Box 2 then adds "Your promotion ({code}) lowers your first {n} statements."
   */
  promo: { code: string; statements: number | null } | null;
  /**
   * The served renewal parts (TMMAPP-0128 item 3). Absent on the subscribe quote, which keeps the
   * amount sentence. When set, box 2 states the plan on `on` plus add-ons, and `trialEnd` stays
   * null — a trial is `trialNotice`.
   */
  parts?: RenewalPartsTerms | null;
  /**
   * `/me.trialEndsOn`, shown beside a parts sentence. Null when the sentence itself names the trial.
   */
  trialNotice?: string | null;
}

/** Plan on `on`, plus seats and connections billed with the statement (TMMAPI-0162 U1). */
export interface RenewalPartsTerms {
  baseMinor: number;
  /**
   * `seatsMinor + connectionsMinor + promotionMinor` (the promotion is negative). The add-ons
   * clause is omitted only when this is 0 and no promotion was served.
   */
  addOnsMinor: number;
  /** True when `promotionMinor` was served, including zero. The clause stays even at 0. */
  includesPromotion: boolean;
  /** Civil `YYYY-MM-DD`. */
  on: string;
}

/** The sentence box 2 was ticked against, as a key: a different sentence needs a fresh tick. */
export function renewalSentenceKey(renewal: RenewalTerms | null): string | null {
  if (renewal === null) return null;
  if (renewal.parts != null) {
    return [
      'parts',
      renewal.interval,
      renewal.currency,
      renewal.parts.baseMinor,
      renewal.parts.addOnsMinor,
      renewal.parts.includesPromotion ? 'promo' : '',
      renewal.parts.on,
    ].join('|');
  }
  return [
    renewal.interval,
    renewal.priceMinor,
    renewal.currency,
    renewal.trialEnd ?? '',
    renewal.promo?.code ?? '',
    renewal.promo?.statements ?? '',
  ].join('|');
}
